import { useState, useCallback, lazy, Suspense } from "react";
import { FileImage, Upload, Download, Trash2, Image, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import SEOHead from "@/components/SEOHead";
import FAQSection from "@/components/FAQSection";
import RelatedTools from "@/components/RelatedTools";
import ToolContentSection from "@/components/ToolContentSection";
import { toolContent } from "@/data/toolContent";
import { downloadFile, downloadZip } from "@/utils/downloadHelper";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  {
    question: "Is this PDF to image converter free?",
    answer: "Yes, our PDF to image converter is completely free to use. No signup, no payment, no hidden fees. Convert unlimited PDFs to images."
  },
  {
    question: "What image formats are supported?",
    answer: "We support PNG (best quality, larger file size) and JPEG (smaller file size, slight quality loss). Choose PNG for documents with text, JPEG for photos."
  },
  {
    question: "Are my files stored on your servers?",
    answer: "No, your files are automatically deleted after 30 minutes. We prioritize your privacy and never permanently store your documents."
  },
  {
    question: "What is DPI and which should I choose?",
    answer: "DPI (Dots Per Inch) determines image quality. Use 72 DPI for web viewing, 150 DPI for standard use, and 300 DPI for printing or high-quality needs."
  },
  {
    question: "Is there a file size limit?",
    answer: "Yes, the maximum file size is 50MB per PDF. For larger files, consider compressing your PDF first using our PDF compress tool."
  }
];

const PdfToImagePage = () => {
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState("png");
  const [dpi, setDpi] = useState("150");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [results, setResults] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const loadPreview = async (pdfFile) => {
    setIsLoadingPreview(true);
    const formData = new FormData();
    formData.append("file", pdfFile);

    try {
      const response = await axios.post(`${API}/pdf-preview`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setPreview(response.data);
      }
    } catch (error) {
      console.error("Preview error:", error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === "application/pdf") {
      setFile(droppedFile);
      setResults(null);
      setPreview(null);
      await loadPreview(droppedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  }, []);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      setFile(selectedFile);
      setResults(null);
      setPreview(null);
      await loadPreview(selectedFile);
    } else {
      toast.error("Please upload a PDF file");
    }
  };

  const handleConvert = async () => {
    if (!file) {
      toast.error("Please select a PDF file first");
      return;
    }

    setIsProcessing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", format);
    formData.append("dpi", dpi);

    try {
      const response = await axios.post(`${API}/pdf-to-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setResults(response.data);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Conversion failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const downloadUrl = `${API}/download/${fileId}`;
      await downloadFile(downloadUrl, filename);
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleDownloadAll = async () => {
    if (results?.files?.length > 0) {
      try {
        const folderId = results.files[0].id.split("/")[0];
        await downloadZip(`${API}/download-all/${folderId}`, "images.zip");
      } catch (error) {
        toast.error("Download failed");
      }
    }
  };

  const handleReset = () => {
    setFile(null);
    setResults(null);
    setPreview(null);
  };

  return (
    <>
      <SEOHead
        title="PDF to Image Converter Free Online | No Signup"
        description="Convert PDF to PNG or JPG images instantly. Free online PDF to image converter with high quality output. No signup required. Files auto-delete."
        canonicalPath="/pdf-to-image"
        keywords="PDF to image, PDF to PNG, PDF to JPG, convert PDF to image, free PDF converter, online PDF to image"
      />
      
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#FF3B30] flex items-center justify-center">
                <FileImage className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight">
                PDF to Image Converter Online Free
              </h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl">
              Convert each page of your PDF into high-quality PNG or JPG images. Perfect for presentations, 
              social media, or when you need to extract images from documents. No signup required.
            </p>
          </div>

          {/* Upload Area */}
          {!results && (
            <div className="space-y-6 animate-fade-in-up animate-delay-100">
              <div
                className={`upload-zone ${isDragging ? "dragging" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
                data-testid="upload-zone"
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                {file ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-secondary flex items-center justify-center mx-auto mb-4">
                      <FileImage className="w-8 h-8 text-[#FF3B30]" />
                    </div>
                    <p className="font-medium text-lg">{file.name}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                      {preview && ` • ${preview.total_pages} pages`}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-secondary flex items-center justify-center">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-lg">Drop your PDF here</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        or click to browse (max 50MB)
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Preview Section */}
              {isLoadingPreview && (
                <div className="bg-card border border-border p-6 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#FF3B30]" />
                  <p className="text-sm text-muted-foreground">Loading preview...</p>
                </div>
              )}

              {preview && !isLoadingPreview && (
                <div className="bg-card border border-border p-4" data-testid="preview-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-4 h-4 text-[#FF3B30]" />
                    <span className="text-sm font-medium">Preview ({preview.total_pages} pages)</span>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {preview.pages.slice(0, 8).map((page, index) => (
                      <div key={index} className="relative aspect-[3/4] bg-secondary overflow-hidden border border-border">
                        <img
                          src={`${API}/download/${preview.preview_id}/${page.thumbnail.split('/').pop()}`}
                          alt={`PDF page ${page.page} thumbnail preview for conversion to ${format.toUpperCase()} image`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-0.5">
                          {page.page}
                        </span>
                      </div>
                    ))}
                    {preview.total_pages > 8 && (
                      <div className="aspect-[3/4] bg-secondary flex items-center justify-center border border-border">
                        <span className="text-sm text-muted-foreground">+{preview.total_pages - 8}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-mono uppercase tracking-wide text-muted-foreground mb-2">
                    Output Format
                  </label>
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger className="w-full rounded-sm" data-testid="format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="png">PNG (Best Quality)</SelectItem>
                      <SelectItem value="jpeg">JPEG (Smaller Size)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-mono uppercase tracking-wide text-muted-foreground mb-2">
                    Quality (DPI)
                  </label>
                  <Select value={dpi} onValueChange={setDpi}>
                    <SelectTrigger className="w-full rounded-sm" data-testid="dpi-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="72">72 DPI (Web)</SelectItem>
                      <SelectItem value="150">150 DPI (Standard)</SelectItem>
                      <SelectItem value="300">300 DPI (High Quality)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Convert Button */}
              <Button
                onClick={handleConvert}
                disabled={!file || isProcessing}
                className="w-full h-14 bg-black hover:bg-black/90 text-white rounded-sm btn-hover-lift text-lg font-medium"
                data-testid="convert-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Converting...
                  </>
                ) : (
                  <>
                    <Image className="w-5 h-5 mr-2" />
                    Convert to {format.toUpperCase()}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-6 animate-fade-in-up" data-testid="results-section">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold tracking-tight">
                  Converted Images
                </h2>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    className="rounded-sm"
                    data-testid="reset-button"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Start Over
                  </Button>
                  <Button
                    onClick={handleDownloadAll}
                    className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 rounded-sm btn-hover-lift"
                    data-testid="download-all-button"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download All (ZIP)
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {results.files.map((file, index) => (
                  <div
                    key={file.id}
                    className="border border-border hover:border-black transition-colors p-4 animate-fade-in-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`result-file-${index}`}
                  >
                    <div className="aspect-[3/4] bg-secondary flex items-center justify-center mb-3">
                      <FileImage className="w-12 h-12 text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium truncate">{file.filename}</p>
                    <p className="text-xs text-muted-foreground mb-3">Page {file.page}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(file.id, file.filename)}
                      className="w-full rounded-sm"
                      data-testid={`download-file-${index}`}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>

              <div className="bg-secondary p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  ⏱ Files will be automatically deleted in 30 minutes
                </p>
              </div>
            </div>
          )}

          {/* SEO Content Section */}
          <ToolContentSection toolKey="pdf-to-image" content={toolContent["pdf-to-image"]} />

          {/* FAQ Section */}
          <FAQSection faqs={faqs} />

          {/* Related Tools */}
          <RelatedTools currentTool="pdf-to-image" />
        </div>
      </div>
    </>
  );
};

export default PdfToImagePage;
